import React from 'react';
import './SkeletonLoader.css';

/**
 * Skeleton Loader Component
 * Provides loading placeholders with shimmer animation
 */
const SkeletonLoader = ({ 
    type = 'text', 
    width = '100%', 
    height = 'auto',
    count = 1,
    className = '',
    style = {}
}) => {
    const renderSkeleton = (index) => {
        const baseProps = {
            key: index,
            className: `skeleton skeleton-${type} ${className}`,
            style: {
                width,
                height: height !== 'auto' ? height : undefined,
                ...style
            }
        };

        switch (type) {
            case 'text':
                return <div {...baseProps} style={{ ...baseProps.style, height: '1rem' }} />;
            
            case 'title':
                return <div {...baseProps} style={{ ...baseProps.style, height: '1.5rem' }} />;
            
            case 'paragraph':
                return (
                    <div key={index} className="skeleton-paragraph">
                        <div className="skeleton skeleton-text" style={{ width: '100%', height: '1rem' }} />
                        <div className="skeleton skeleton-text" style={{ width: '90%', height: '1rem' }} />
                        <div className="skeleton skeleton-text" style={{ width: '75%', height: '1rem' }} />
                    </div>
                );
            
            case 'circle':
                return <div {...baseProps} style={{ ...baseProps.style, borderRadius: '50%', aspectRatio: '1' }} />;
            
            case 'avatar':
                return <div {...baseProps} style={{ ...baseProps.style, width: '40px', height: '40px', borderRadius: '50%' }} />;
            
            case 'button':
                return <div {...baseProps} style={{ ...baseProps.style, height: '2.5rem', borderRadius: '6px' }} />;
            
            case 'card':
                return (
                    <div key={index} className="skeleton-card">
                        <div className="skeleton skeleton-rectangle" style={{ width: '100%', height: '200px', borderRadius: '8px 8px 0 0' }} />
                        <div className="skeleton-card-content">
                            <div className="skeleton skeleton-text" style={{ width: '80%', height: '1.25rem', marginBottom: '8px' }} />
                            <div className="skeleton skeleton-text" style={{ width: '60%', height: '1rem', marginBottom: '12px' }} />
                            <div className="skeleton skeleton-text" style={{ width: '90%', height: '0.875rem' }} />
                            <div className="skeleton skeleton-text" style={{ width: '70%', height: '0.875rem' }} />
                        </div>
                    </div>
                );
            
            case 'table-row':
                return (
                    <div key={index} className="skeleton-table-row">
                        <div className="skeleton skeleton-text" style={{ width: '20%', height: '1rem' }} />
                        <div className="skeleton skeleton-text" style={{ width: '30%', height: '1rem' }} />
                        <div className="skeleton skeleton-text" style={{ width: '15%', height: '1rem' }} />
                        <div className="skeleton skeleton-text" style={{ width: '25%', height: '1rem' }} />
                        <div className="skeleton skeleton-text" style={{ width: '10%', height: '1rem' }} />
                    </div>
                );
            
            case 'property-card':
                return (
                    <div key={index} className="skeleton-property-card">
                        <div className="skeleton skeleton-rectangle" style={{ width: '100%', height: '160px', borderRadius: '12px 12px 0 0' }} />
                        <div className="skeleton-property-content">
                            <div className="skeleton skeleton-text" style={{ width: '85%', height: '1.1rem', marginBottom: '6px' }} />
                            <div className="skeleton skeleton-text" style={{ width: '70%', height: '0.9rem' }} />
                        </div>
                    </div>
                );
            
            case 'dashboard-counter':
                return (
                    <div key={index} className="skeleton-counter">
                        <div className="skeleton skeleton-circle" style={{ width: '48px', height: '48px' }} />
                        <div className="skeleton-counter-content">
                            <div className="skeleton skeleton-text" style={{ width: '60px', height: '2rem', marginBottom: '4px' }} />
                            <div className="skeleton skeleton-text" style={{ width: '100px', height: '1rem', marginBottom: '4px' }} />
                            <div className="skeleton skeleton-text" style={{ width: '120px', height: '0.85rem' }} />
                        </div>
                    </div>
                );
            
            case 'list-item':
                return (
                    <div key={index} className="skeleton-list-item">
                        <div className="skeleton skeleton-circle" style={{ width: '32px', height: '32px' }} />
                        <div className="skeleton-list-content">
                            <div className="skeleton skeleton-text" style={{ width: '70%', height: '1rem', marginBottom: '4px' }} />
                            <div className="skeleton skeleton-text" style={{ width: '50%', height: '0.875rem' }} />
                        </div>
                    </div>
                );
            
            case 'rectangle':
            default:
                return <div {...baseProps} />;
        }
    };

    if (count === 1) {
        return renderSkeleton(0);
    }

    return (
        <div className="skeleton-container">
            {Array.from({ length: count }, (_, index) => renderSkeleton(index))}
        </div>
    );
};

/**
 * Skeleton Table Component
 */
export const SkeletonTable = ({ rows = 5, columns = 4 }) => (
    <div className="skeleton-table">
        <div className="skeleton-table-header">
            {Array.from({ length: columns }, (_, i) => (
                <div key={i} className="skeleton skeleton-text" style={{ height: '1rem' }} />
            ))}
        </div>
        {Array.from({ length: rows }, (_, i) => (
            <div key={i} className="skeleton-table-row">
                {Array.from({ length: columns }, (_, j) => (
                    <div key={j} className="skeleton skeleton-text" style={{ height: '1rem' }} />
                ))}
            </div>
        ))}
    </div>
);

/**
 * Skeleton Grid Component
 */
export const SkeletonGrid = ({ items = 6, type = 'card' }) => (
    <div className="skeleton-grid">
        {Array.from({ length: items }, (_, i) => (
            <SkeletonLoader key={i} type={type} />
        ))}
    </div>
);

/**
 * Skeleton Dashboard Component
 */
export const SkeletonDashboard = () => (
    <div className="skeleton-dashboard">
        <div className="skeleton-dashboard-header">
            <SkeletonLoader type="title" width="300px" />
            <SkeletonLoader type="text" width="200px" />
        </div>
        <div className="skeleton-counters-grid">
            {Array.from({ length: 8 }, (_, i) => (
                <SkeletonLoader key={i} type="dashboard-counter" />
            ))}
        </div>
        <div className="skeleton-properties-section">
            <SkeletonLoader type="title" width="200px" />
            <SkeletonGrid items={6} type="property-card" />
        </div>
    </div>
);

export default SkeletonLoader;


